/* eslint-disable complexity */
import React, { FC, Fragment, useMemo } from 'react';
import { Box, ButtonBase, Divider, Drawer, Stack, Typography } from '@mui/material';
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useOrderItemList, useOrderItemListRows } from '@/data/Content/OrderItemList';
import { useLocalization } from '@/data/Localization';
import { OrderItem } from '@/data/types/Order';
import { formatPrice } from '@/utils/formatPrice';
import { HeaderMiniCartDropMenuItem } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItem';
import ClearIcon from '@mui/icons-material/Clear';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { useOrderItemListRowsAvailability } from '@/data/Content/OrderItemInventory';
import { useNotifications } from '@/data/Content/Notifications';
import { useMobileHeaderQuery } from '..';
import { Img } from '@/components/blocks/MaterialImage';

export const HeaderMiniCartDrawerMenu: FC<{
	fromLocal: boolean;
	onClose: () => void;
	// eslint-disable-next-line complexity
}> = ({ fromLocal, onClose }) => {
	const CartLabels = useLocalization('MiniCart');
	const RouteLabels = useLocalization('Routes');
	const { orderItems, data: order, checkout, canContinue, getCount, router } = useCart();
	const totalPrice = order?.totalProductPrice ?? '0.00';
	const isMobile = useMobileHeaderQuery();
	const MAX_ROWS = isMobile ? 2 : 3;
	const { firstNRows } = useMemo(() => {
		const firstNRows = orderItems?.slice(0, MAX_ROWS);
		const firstNRowsCount = getCount(firstNRows);
		return { firstNRows, firstNRowsCount };
	}, [MAX_ROWS, getCount, orderItems]);

	const orderItemList = useOrderItemList(orderItems as OrderItem[], order?.orderId || '');
	const orderItemListRows = useOrderItemListRows(orderItemList.data);
	const { showSuccessMessage } = useNotifications();

	const orderItemListRowsSorted = orderItemListRows.data.sort((a, b) => {
		const nameA = a.details.name || '';
		const nameB = b.details.name || '';
		return nameA.localeCompare(nameB);
	});

	const {
		orderItemListRowsWithAvailability,
		orderItemListRowsNotAvailable,
		loadingOrderItemListRowsAvailability,
	} = useOrderItemListRowsAvailability(orderItemListRowsSorted);

	const loading =
		orderItemListRowsWithAvailability.length === 0 || loadingOrderItemListRowsAvailability;

	const disabled = loading ? true : canContinue ? !canContinue() : false;

	const handleOnClickCheckout = () => {
		if (orderItemListRowsNotAvailable.length > 0) {
			router.push(RouteLabels.Cart.route.t()).finally(() => {
				showSuccessMessage(
					'Revisa tus productos del carrito y elimina el producto caducado para continuar tu compra.',
					'TIENES UN PRODUCTO CADUCADO'
				);
			});

			return;
		}

		checkout();
	};

	const handleOnClickCart = () => {
		const path = window.location.pathname;
		if (path.includes(RouteLabels.Cart.route.t())) {
			onClose();
			return;
		}
		router.push(RouteLabels.Cart.route.t());
	};

	return (
		<Drawer
			open
			onClose={onClose}
			sx={{ zIndex: 9100 }}
			ModalProps={{
				keepMounted: true,
				disableScrollLock: true,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					height: 'auto',
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 1000,
					borderTopLeftRadius: '10px',
					borderTopRightRadius: '10px',
					backgroundColor: '#FFFFFF',
				}}
			>
				{firstNRows?.length > 0 ? (
					<Fragment>
						<ClearIcon
							sx={{
								fontSize: 'var(--font-size-xl)',
								cursor: 'pointer',
								position: 'absolute',
								top: '14px',
								right: '14px',
							}}
							onClick={onClose}
						/>
						<Box
							sx={{
								width: '100%',
								paddingTop: '30px',
								paddingX: '30px',
								paddingBottom: '38px',
							}}
						>
							<Typography
								sx={{
									fontSize: 'var(--font-size-lg)',
									color: '#2C2C2C',
									fontWeight: 800,
									display: 'flex',
									alignItems: 'center',
									marginBottom: '20px',
									textTransform: 'uppercase',
								}}
							>
								{!fromLocal ? (
									<Img
										src="/images/check-circle.png"
										alt="check-circle"
										sx={{ height: '27px', width: '27px', marginRight: '11px' }}
									/>
								) : null}
								{fromLocal ? 'Carrito de compras' : 'AGREGASTE AL CARRITO'}
							</Typography>
							<Stack
								spacing={1}
								sx={{
									maxHeight: 250,
									overflowY: 'auto',
								}}
							>
								{!loading ? (
									<React.Fragment>
										{orderItemListRowsWithAvailability.map((product) => (
											<HeaderMiniCartDropMenuItem
												key={product.orderItemListRow.orderItem.orderItemId}
												thumbnail={product.details.thumbnail}
												name={product.details.name}
												attributes={product.details.attributes}
												offerPrice={product.details.prices.offer || 0}
												displayPrice={product.details.prices.list || 0}
												totalAdjustment={product.orderItemListRow.totalAdjustment.totalAdjustment}
												orderItemPrice={product.orderItemListRow.price.orderItemPrice}
												exhausted={product.availability?.status === false}
												variant="drawer"
											/>
										))}
									</React.Fragment>
								) : (
									<HeaderMiniCartDropMenuItemSkeleton variant="drawer" />
								)}
							</Stack>
							<Divider sx={{ marginTop: '20px', marginBottom: '11px' }} />
							<Stack direction="row" justifyContent="space-between">
								<Typography>{'Total:'}</Typography>
								<Typography
									sx={{
										fontWeight: 800,
									}}
								>
									{formatPrice('es-MX', 'MXN', totalPrice)}
								</Typography>
							</Stack>
							<Stack
								direction="row"
								spacing="14px"
								alignItems="center"
								justifyContent={'center'}
								sx={{
									marginTop: '30px',
								}}
							>
								<ButtonBase
									sx={{
										backgroundColor: '#F1F1F1',
										color: '#2C2C2C',
										borderRadius: 50,
										height: '46px',
										paddingLeft: '20px',
										paddingRight: '20px',
										fontWeight: 600,
										fontSize: 'var(--font-size-body)',
										minWidth: '48%',
									}}
									onClick={handleOnClickCart}
								>
									{`Ver carrito ${
										orderItemListRowsWithAvailability.length > 0
											? `(${orderItemListRowsWithAvailability.length})`
											: ''
									}`}
								</ButtonBase>
								<ButtonBase
									sx={{
										backgroundColor: '#011688',
										color: '#FFFFFF',
										borderRadius: 50,
										height: '46px',
										paddingLeft: '20px',
										paddingRight: '20px',
										fontWeight: 600,
										minWidth: '48.5%',
										fontSize: 'var(--font-size-body)',
										':disabled': {
											backgroundColor: '#B5B5B5',
											color: '#FFFFFF',
										},
									}}
									disabled={disabled}
									onClick={handleOnClickCheckout}
								>
									{'Ir a pagar'}
								</ButtonBase>
							</Stack>
						</Box>
					</Fragment>
				) : (
					<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
						<Typography variant="body1" align="center">
							{CartLabels.Empty.t()}
						</Typography>
					</Stack>
				)}
			</Box>
		</Drawer>
	);
};
